/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';

import { Button, useAccordionContext } from '@coursera/cds-core';
import { ChevronDownIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedButton from 'bundles/page/components/TrackedButton';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

type Props = {
  label: string;
  ariaLabel?: string;
  trackingName?: string;
};

const styles = {
  button: css`
    max-width: 250px;
    // show button label when button is in focus
    &:focus span {
      display: inherit;
    }

    svg {
      transition: transform 150ms ease-in-out;
      transform: rotate(0deg);
    }

    &[aria-expanded='true'] {
      svg {
        transform: rotate(180deg);
      }
    }
  `,
  label: css`
    display: none;
  `,
};

const ToggleButton = ({ label, trackingName = 'accordion', ariaLabel }: Props) => {
  const context = useAccordionContext();
  const eventingProductData = useEventingProductData();
  const track = useTracker();

  const onClick = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const onToggleExpand = context?.onToggleExpanded;

      if (context?.expanded === false) {
        track('expand_accordion', { accordion: { name: 'course_details' }, ...eventingProductData });
      } else if (context?.expanded === true) {
        track('collapse_accordion', { accordion: { name: 'course_details' }, ...eventingProductData });
      }

      if (onToggleExpand) {
        onToggleExpand(event);
      }
    },
    [context, eventingProductData, track]
  );

  return (
    <Button
      aria-expanded={context?.expanded || false}
      onClick={onClick}
      css={styles.button}
      variant="ghost"
      icon={<ChevronDownIcon size="medium" color="interactive" />}
      iconPosition="after"
      component={TrackedButton}
      trackingName={context?.expanded ? `${trackingName}_collapse` : `${trackingName}_expand`}
      withVisibilityTracking={false}
      requireFullyVisible={false}
      aria-label={ariaLabel ?? label}
    >
      <span css={styles.label}>{label}</span>
    </Button>
  );
};

export default ToggleButton;
